// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, Share } from 'react-native';

export type ShareImageOutcome = 'shared' | 'downloaded' | 'cancelled' | 'unsupported';

type ExpoFileSystemModule = {
  Paths: { cache: unknown };
  File: new (...parts: unknown[]) => {
    uri: string;
    exists: boolean;
    create: (options?: { overwrite?: boolean; intermediates?: boolean }) => void;
    write: (content: string, options?: { encoding?: string }) => void;
  };
};

type ExpoSharingModule = {
  isAvailableAsync: () => Promise<boolean>;
  shareAsync: (
    uri: string,
    options?: { mimeType?: string; UTI?: string; dialogTitle?: string }
  ) => Promise<void>;
};

/**
 * expo-file-system / expo-sharing are loaded lazily and defensively.
 *
 * Both are native modules, so they are absent from the web bundle and from any build made
 * before they were added to package.json. A missing module must degrade to a web download or
 * an "unsupported" result rather than taking the leaderboard screen down with it.
 */
const loadFileSystem = (): ExpoFileSystemModule | null => {
  try {
    // Metro only resolves require() with a literal, so both loaders repeat the call site.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fs = require('expo-file-system') as ExpoFileSystemModule;
    return fs?.Paths?.cache && fs.File ? fs : null;
  } catch {
    return null;
  }
};

const loadSharing = (): ExpoSharingModule | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const sharing = require('expo-sharing') as ExpoSharingModule;
    return typeof sharing?.shareAsync === 'function' ? sharing : null;
  } catch {
    return null;
  }
};

const isCancellation = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return message.includes('cancel') || message.includes('abort');
};

/** Writes the PNG into the app cache and returns its file:// URI. */
const writeCacheFile = (base64: string, fileName: string): string => {
  const fs = loadFileSystem();
  if (!fs) {
    throw new Error('expo-file-system unavailable');
  }
  const file = new fs.File(fs.Paths.cache, fileName);
  file.create({ overwrite: true, intermediates: true });
  file.write(base64, { encoding: 'base64' });
  return file.uri;
};

const shareOnWeb = async (
  base64: string,
  fileName: string,
  title: string
): Promise<ShareImageOutcome> => {
  const blob = await (await fetch(`data:image/png;base64,${base64}`)).blob();
  const nav = navigator as Navigator & {
    canShare?: (data: { files?: File[] }) => boolean;
    share?: (data: { title?: string; text?: string; files?: File[] }) => Promise<void>;
  };

  if (typeof File === 'function' && typeof nav.share === 'function') {
    const file = new File([blob], fileName, { type: 'image/png' });
    // canShare is the only reliable way to know the browser accepts file payloads;
    // desktop Chrome exposes navigator.share but rejects files with a TypeError.
    if (nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({ title, files: [file] });
        return 'shared';
      } catch (error) {
        if (isCancellation(error)) {
          return 'cancelled';
        }
        // fall through to the download path
      }
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously cancels the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
};

const shareOnNative = async (
  base64: string,
  fileName: string,
  title: string
): Promise<ShareImageOutcome> => {
  const uri = writeCacheFile(base64, fileName);
  const sharing = loadSharing();

  if (sharing) {
    if (!(await sharing.isAvailableAsync())) {
      return 'unsupported';
    }
    try {
      await sharing.shareAsync(uri, { mimeType: 'image/png', UTI: 'public.png', dialogTitle: title });
      return 'shared';
    } catch (error) {
      return isCancellation(error) ? 'cancelled' : 'unsupported';
    }
  }

  // Fallback for a build that predates expo-sharing. React Native's own Share sheet carries a
  // file URL on iOS only — on Android the url field is ignored, so there is nothing to send.
  if (Platform.OS !== 'ios') {
    return 'unsupported';
  }
  try {
    const result = await Share.share({ title, url: uri });
    return result.action === Share.dismissedAction ? 'cancelled' : 'shared';
  } catch (error) {
    return isCancellation(error) ? 'cancelled' : 'unsupported';
  }
};

/**
 * Hands a base64 PNG to the platform share sheet.
 *
 * `base64` is the raw payload without the `data:image/png;base64,` prefix, which is exactly what
 * react-native-svg's `toDataURL` returns on both web and native.
 */
export const sharePngBase64 = async (
  base64: string,
  fileName: string,
  title: string
): Promise<ShareImageOutcome> =>
  Platform.OS === 'web'
    ? shareOnWeb(base64, fileName, title)
    : shareOnNative(base64, fileName, title);
